// Thin EventSource wrapper with one-shot ticket exchange + automatic reconnect.
// Tickets prevent long-lived JWT tokens from appearing in server access logs/URLs.
// Legacy ?token= path is no longer used by this client (deprecated on BE).

import { apiPost } from "@/lib/api/client";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000;

export interface SSEOptions {
  /** JWT access token — used only to obtain a one-shot ticket; NOT sent in the URL. */
  token: string;
  /** Exact topic to subscribe to, e.g. "order.paid.{uuid}" */
  topic: string;
  onMessage: (data: unknown) => void;
  onError?: (err: Event) => void;
}

export interface SSEClient {
  close: () => void;
}

/**
 * Opens an SSE connection to /v1/events?topic=...&ticket=...
 *
 * Before each connect attempt (including reconnects):
 *   1. POST /v1/sse/ticket to exchange Bearer auth for a one-shot ticket (30 s TTL).
 *   2. Open EventSource with ?ticket= query param.
 *
 * Reconnects on close with exponential backoff (up to MAX_RETRIES times).
 * Returns a handle with a close() method.
 */
export function openSSE(opts: SSEOptions): SSEClient {
  let es: EventSource | null = null;
  let retries = 0;
  let closed = false;

  async function connect() {
    if (closed) return;

    // Step 1: obtain a one-shot ticket via authenticated API call.
    let ticket: string;
    try {
      const res = await apiPost<{ ticket: string }>("/v1/sse/ticket");
      ticket = res.ticket;
    } catch (err) {
      // 401 means token expired — surface to caller via onError.
      const fakeEvent = new Event("error");
      opts.onError?.(fakeEvent);
      // Attempt reconnect with backoff (token may refresh before next try).
      if (!closed && retries < MAX_RETRIES) {
        const delay = BASE_DELAY_MS * 2 ** retries;
        retries++;
        setTimeout(() => { void connect(); }, delay);
      }
      return;
    }

    if (closed) return;

    // Step 2: open EventSource with the one-shot ticket in query param.
    const url = `${API_BASE}/v1/events?topic=${encodeURIComponent(opts.topic)}&ticket=${encodeURIComponent(ticket)}`;
    es = new EventSource(url);

    es.onmessage = (ev) => {
      retries = 0; // reset on successful message
      try {
        opts.onMessage(JSON.parse(ev.data as string));
      } catch {
        opts.onMessage(ev.data);
      }
    };

    es.onerror = (err) => {
      opts.onError?.(err);
      es?.close();
      es = null;

      if (closed || retries >= MAX_RETRIES) return;
      const delay = BASE_DELAY_MS * 2 ** retries;
      retries++;
      // Each reconnect requests a fresh ticket (tickets are one-shot).
      setTimeout(() => { void connect(); }, delay);
    };
  }

  void connect();

  return {
    close() {
      closed = true;
      es?.close();
      es = null;
    },
  };
}
